import { useEffect, useState, type ReactNode } from "react";

import { cx } from "../app/format";

export const DASHBOARD_LOADING_TITLE = "Getting your projects ready";
export const DASHBOARD_LOADING_MESSAGE =
  "Your saved projects will appear here in a moment.";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function prefersReducedMotion(): boolean {
  return (
    typeof globalThis.window !== "undefined" &&
    typeof globalThis.window.matchMedia === "function" &&
    globalThis.window.matchMedia(REDUCED_MOTION_QUERY).matches
  );
}

function usePrefersReducedMotion(): boolean {
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);

  useEffect(() => {
    if (typeof globalThis.window === "undefined") {
      return undefined;
    }

    const mediaQuery = globalThis.window.matchMedia?.(REDUCED_MOTION_QUERY);

    if (!mediaQuery) {
      return undefined;
    }

    const handleChange = (event: MediaQueryListEvent) => {
      setReducedMotion(event.matches);
    };

    mediaQuery.addEventListener?.("change", handleChange);

    return () => {
      mediaQuery.removeEventListener?.("change", handleChange);
    };
  }, []);

  return reducedMotion;
}

function SkeletonBlock({
  className,
  reducedMotion
}: {
  className: string;
  reducedMotion: boolean;
}) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        "tcsDashboardSkeletonBlock block rounded-[7px]",
        !reducedMotion && "tcsDashboardSkeletonShimmer",
        className
      )}
    />
  );
}

function DashboardSkeleton({ reducedMotion }: { reducedMotion: boolean }) {
  return (
    <div aria-hidden="true" data-dashboard-loading-skeleton>
      <section className="grid gap-3 py-6 md:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 8 }, (_, index) => (
          <div
            key={`metric-${index}`}
            className="rounded-[12px] border border-[var(--line)] bg-[var(--surface)] px-4 py-3"
          >
            <SkeletonBlock reducedMotion={reducedMotion} className="h-2.5 w-20" />
            <SkeletonBlock reducedMotion={reducedMotion} className="mt-3 h-7 w-14" />
          </div>
        ))}
      </section>

      <div className="mb-4 grid gap-3 xl:grid-cols-[minmax(260px,1fr)_auto]">
        <div className="flex min-w-0 items-center justify-between gap-3 rounded-[10px] border border-[var(--line)] bg-[var(--surface)] px-3 py-2">
          <SkeletonBlock reducedMotion={reducedMotion} className="h-3 w-28" />
          <SkeletonBlock reducedMotion={reducedMotion} className="h-3 w-8" />
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          {Array.from({ length: 4 }, (_, index) => (
            <SkeletonBlock
              key={`filter-${index}`}
              reducedMotion={reducedMotion}
              className="h-9 w-16 rounded-[10px]"
            />
          ))}
        </div>
      </div>

      <section className="grid gap-3">
        {Array.from({ length: 2 }, (_, index) => (
          <article
            key={`project-${index}`}
            className="grid min-h-[188px] gap-4 rounded-[13px] border border-[var(--line)] bg-[var(--surface)] px-5 py-4 xl:grid-cols-[minmax(260px,.9fr)_minmax(0,1.85fr)]"
          >
            <div className="flex min-w-0 flex-col justify-between gap-4">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <SkeletonBlock
                    reducedMotion={reducedMotion}
                    className="h-6 w-44 rounded-[6px]"
                  />
                  <SkeletonBlock
                    reducedMotion={reducedMotion}
                    className="h-6 w-20 rounded-full"
                  />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <SkeletonBlock reducedMotion={reducedMotion} className="h-3 w-28" />
                  <SkeletonBlock reducedMotion={reducedMotion} className="h-3 w-24" />
                  <SkeletonBlock reducedMotion={reducedMotion} className="h-3 w-20" />
                </div>
                <SkeletonBlock reducedMotion={reducedMotion} className="mt-4 h-3 w-48" />
              </div>
              <SkeletonBlock
                reducedMotion={reducedMotion}
                className="h-10 w-28 rounded-[10px]"
              />
            </div>

            <div className="grid gap-3 lg:grid-cols-[1.05fr_.95fr]">
              <div>
                <SkeletonBlock reducedMotion={reducedMotion} className="h-2.5 w-20" />
                <SkeletonBlock
                  reducedMotion={reducedMotion}
                  className="mt-2 h-12 w-full rounded-[10px]"
                />
              </div>
              <div>
                <SkeletonBlock reducedMotion={reducedMotion} className="h-2.5 w-16" />
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  {Array.from({ length: 4 }, (_, metricIndex) => (
                    <SkeletonBlock
                      key={`project-${index}-metric-${metricIndex}`}
                      reducedMotion={reducedMotion}
                      className="h-12 rounded-[10px]"
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="xl:col-span-2 rounded-[12px] border border-[var(--line)] bg-[var(--raised2)] px-4 py-3">
              <SkeletonBlock reducedMotion={reducedMotion} className="h-2.5 w-32" />
              <div className="mt-2 flex flex-wrap gap-2">
                <SkeletonBlock reducedMotion={reducedMotion} className="h-6 w-32" />
                <SkeletonBlock reducedMotion={reducedMotion} className="h-6 w-36" />
                <SkeletonBlock reducedMotion={reducedMotion} className="h-6 w-28" />
              </div>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}

export function DashboardLoadingState({
  technicalDetails
}: {
  technicalDetails?: ReactNode;
}) {
  const reducedMotion = usePrefersReducedMotion();

  return (
    <main
      id="main-content"
      tabIndex={-1}
      aria-label="Projects dashboard"
      aria-busy="true"
      className="min-h-screen px-8 py-8 xl:px-14"
    >
      <div className="mx-auto max-w-[1320px]">
        <header className="flex flex-wrap items-start justify-between gap-5 border-b border-[var(--line)] pb-6">
          <div>
            <div className="flex items-center gap-[11px]">
              <div
                aria-hidden="true"
                className="flex h-[32px] w-[32px] flex-none items-center justify-center rounded-lg bg-[var(--ink)]"
              >
                <div className="h-[14px] w-[14px] rounded-[3px] border-[2.5px] border-[var(--bg)]" />
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[.11em] text-[var(--faint)]">
                  Titan Specifier
                </div>
                <h1 className="text-[34px] font-extrabold leading-none tracking-[-.035em]">
                  {DASHBOARD_LOADING_TITLE}
                </h1>
              </div>
            </div>
            <p className="mt-3 max-w-[680px] text-sm leading-[1.55] text-[var(--muted)]">
              {DASHBOARD_LOADING_MESSAGE}
            </p>
          </div>
          <div aria-hidden="true" className="flex flex-wrap items-center gap-2">
            <SkeletonBlock
              reducedMotion={reducedMotion}
              className="h-10 w-28 rounded-[10px]"
            />
            <SkeletonBlock
              reducedMotion={reducedMotion}
              className="h-10 w-24 rounded-[10px]"
            />
          </div>
        </header>

        <DashboardSkeleton reducedMotion={reducedMotion} />

        {technicalDetails ? <div className="mt-5">{technicalDetails}</div> : null}
      </div>

      <p role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {DASHBOARD_LOADING_TITLE}. {DASHBOARD_LOADING_MESSAGE}
      </p>
    </main>
  );
}
